import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Award, Calendar, MapPin, Ruler, Shield, Target, User, Weight, DollarSign, BarChart2, ArrowRightLeft, TrendingUp } from 'lucide-react';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import { 
  fetchNhlPlayerLanding, 
  fetchNhlPlayerSeasonStats, 
  getAvailableSeasons,
  NhlFeaturedStats, 
  NhlLastGame, 
  NhlPlayerLanding, 
  NhlPlayerName,
  NhlSeasonStats 
} from '../../lib/api/nhlPlayers';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { getPlayerContractInfo } from '../../lib/supabase';
import { useLeagueStore } from '../../lib/leagueStore';
import { useEmbedStore } from '../../lib/embedStore';
import './PlayerDetailEmbed.css';
import { PerformanceChart } from './PerformanceChart';
import { ContractValueBadge } from './ContractValueBadge';
import { PlayerTransactionHistory } from './PlayerTransactionHistory';
import { NhlEdgeStatsSection } from './NhlEdgeStatsSection';

interface ContractInfo {
  salary: number;
  yearleft: string; // Changé de number à string car c'est un character varying dans la DB
}

interface SeasonStatsState {
  loading: boolean;
  data: NhlSeasonStats | null;
  error: string | null;
}

interface FetchState {
  loading: boolean;
  player: NhlPlayerLanding | null;
  error: string | null;
  contractInfo: ContractInfo | null;
  contractLoading: boolean;
}

export function PlayerDetailPage() {
  // Tracker cette page
  usePageTracking('playerstats');
  
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const selectedLeague = useLeagueStore((s) => s.selectedLeague);
  const isEmbed = useEmbedStore((s) => s.isEmbed);
  const [state, setState] = useState<FetchState>({ 
    loading: true, 
    player: null, 
    error: null, 
    contractInfo: null, 
    contractLoading: false 
  });

  // Analytics tab state
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState<'chart' | 'contract' | 'transactions'>('contract');

  // États pour les statistiques par saison
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [seasonStats, setSeasonStats] = useState<SeasonStatsState>({
    loading: false,
    data: null,
    error: null
  });

  const availableSeasons = useMemo(() => getAvailableSeasons(), []);

  // Initialiser la saison sélectionnée à la saison actuelle
  useEffect(() => {
    if (availableSeasons.length > 0 && !selectedSeason) {
      // Sélectionner la saison actuelle (2025-26) par défaut
      const currentSeason = availableSeasons.find(s => s.isCurrent);
      if (currentSeason) {
        setSelectedSeason(currentSeason.value);
      } else {
        // Fallback sur la première saison disponible
        setSelectedSeason(availableSeasons[0].value);
      }
    }
  }, [availableSeasons, selectedSeason]);

  // Charger les statistiques de la saison sélectionnée
  useEffect(() => {
    const id = Number(playerId);
    if (!Number.isFinite(id) || id <= 0 || !selectedSeason) return;

    let cancelled = false;

    setSeasonStats(prev => ({ ...prev, loading: true, error: null }));

    fetchNhlPlayerSeasonStats(id, language)
      .then((data) => {
        if (!cancelled) {
          setSeasonStats({ loading: false, data, error: null });
        }
      })
      .catch((error: any) => {
        console.error('❌ Erreur chargement stats:', error);
        if (!cancelled) {
          const message = error?.message || translate('player.detail.fetch.fallback', language);
          setSeasonStats({ loading: false, data: null, error: message });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [playerId, selectedSeason, language]);

  useEffect(() => {
    let cancelled = false;
    const id = Number(playerId);

    if (!Number.isFinite(id) || id <= 0) {
      setState(prev => ({ ...prev, loading: false, player: null, error: translate('player.detail.invalid.id', language) }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    fetchNhlPlayerLanding(id, language)
      .then((player) => {
        if (!cancelled) {
          setState(prev => ({ ...prev, loading: false, player, error: null }));
        }
      })
      .catch((error: any) => {
        if (!cancelled) {
          const message = error?.message || translate('player.detail.fetch.fallback', language);
          setState(prev => ({ ...prev, loading: false, player: null, error: message }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [playerId, language]);

  // Récupérer les informations contractuelles
  useEffect(() => {
    const id = Number(playerId);
    if (!Number.isFinite(id) || id <= 0) return;

    setState(prev => ({ ...prev, contractLoading: true }));

    getPlayerContractInfo(id)
      .then(({ data }) => {
        setState(prev => ({ 
          ...prev, 
          contractLoading: false, 
          contractInfo: data 
        }));
      })
      .catch((error) => {
        console.error('Erreur récupération contrat:', error);
        setState(prev => ({ ...prev, contractLoading: false }));
      });
  }, [playerId]);

  const { player, loading, error, contractInfo, contractLoading } = state;

  const displayName = useMemo(() => {
    if (!player) return '—';
    return `${getLocalized(player.firstName, language)} ${getLocalized(player.lastName, language)}`.trim();
  }, [player, language]);

  const isGoalie = player?.position?.toUpperCase() === 'G';

  return (
    <div className={`container py-4 player-detail-page${isEmbed ? ' player-detail-embed' : ''}`}>
      <div className="d-flex align-items-center gap-2 mb-3">
        <button className="btn btn-outline-secondary" onClick={() => navigate(-1 as any)}>
          <ArrowLeft size={16} className="me-2" />
          {translate('player.detail.back', language)}
        </button>
        {player?.playerSlug && (
          <a
            className="btn btn-link text-decoration-none"
            href={`https://www.nhl.com/player/${player.playerSlug}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {translate('player.detail.open.nhl', language)}
          </a>
        )}
      </div>

      {loading && (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status" aria-label={translate('loading', language)} />
        </div>
      )}

      {!loading && error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!loading && player && (
        <div className="d-flex flex-column gap-4">
          <header className="card shadow-sm overflow-hidden">
            {player.heroImage && (
              <div
                className="player-hero-image"
                style={{
                  backgroundImage: `linear-gradient(180deg, rgba(7,12,19,0.5) 0%, rgba(7,12,19,0.85) 70%), url(${player.heroImage})`,
                }}
              />
            )}
            <div className="card-body">
              <div className="row g-4 align-items-center">
                <div className="col-12 col-md-auto text-center">
                  <div className="player-headshot-wrapper mx-auto">
                    <img
                      src={player.headshot || `https://assets.nhle.com/mugs/nhl/20242025/${player.currentTeamAbbrev ?? 'NHL'}/${player.playerId}.png`}
                      alt={displayName}
                      className="img-fluid rounded"
                      onError={(event) => {
                        (event.currentTarget as HTMLImageElement).src = 'https://assets.nhle.com/mugs/nhl/20242025/NHL/8478402.png';
                      }}
                    />
                  </div>
                </div>
                <div className="col">
                  <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3">
                    <div>
                      <p className="text-uppercase mb-1 text-muted fw-semibold">
                        {player.currentTeamAbbrev || translate('player.detail.team.unknown', language)}
                      </p>
                      <h1 className="display-5 fw-bold mb-2">
                        {displayName}
                        {player.sweaterNumber ? <span className="text-primary ms-3">#{player.sweaterNumber}</span> : null}
                      </h1>
                      <div className="d-flex flex-wrap gap-3 text-muted">
                        <span className="d-flex align-items-center gap-2">
                          <User size={16} />
                          {translate('player.detail.position', language)}{' '}
                          <strong>{player.position || translate('player.detail.position.unknown', language)}</strong>
                        </span>
                        {player.teamPlaceNameWithPreposition && (
                          <span className="d-flex align-items-center gap-2">
                            <MapPin size={16} />
                            {getLocalized(player.teamPlaceNameWithPreposition, language)}
                          </span>
                        )}
                        {player.birthDate && (
                          <span className="d-flex align-items-center gap-2">
                            <Calendar size={16} />
                            {formatDateWithAge(player.birthDate, language)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="badge team-badge p-3 fs-6">
                      {player.fullTeamName ? getLocalized(player.fullTeamName, language) : translate('player.detail.team.unknown', language)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <section className="row g-4">
            <div className="col-12 col-lg-4">
              <div className="card h-100 shadow-sm">
                <div className="card-header bg-transparent">
                  <h2 className="h5 mb-0">{translate('player.detail.biography', language)}</h2>
                </div>
                <div className="card-body d-flex flex-column gap-3">
                  <InfoRow icon={<Ruler size={16} />} label={translate('player.detail.height', language)} value={formatHeight(player, language)} />
                  <InfoRow icon={<Weight size={16} />} label={translate('player.detail.weight', language)} value={formatWeight(player, language)} />
                  <InfoRow icon={<Target size={16} />} label={translate('player.detail.shoots', language)} value={formatShoots(player, language)} />
                  <InfoRow icon={<Shield size={16} />} label={translate('player.detail.draft', language)} value={formatDraft(player, language)} />
                  <InfoRow icon={<MapPin size={16} />} label={translate('player.detail.birthplace', language)} value={formatBirthplace(player, language)} />
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-8">
              <div className="row g-3 h-100">
                <div className="col-12">
                  {/* Analytics Dashboard Section */}
                  <section className="card shadow-sm">
                    <div className="card-header bg-transparent">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <TrendingUp size={18} className="text-primary" />
                        <h2 className="h5 mb-0">{translate('player.detail.analytics.title', language)}</h2>
                      </div>
                      <ul className="nav nav-tabs card-header-tabs" role="tablist">
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link d-flex align-items-center gap-1${activeAnalyticsTab === 'chart' ? ' active' : ''}`}
                            onClick={() => setActiveAnalyticsTab('chart')}
                            type="button"
                            role="tab"
                          >
                            <BarChart2 size={14} />
                            {translate('player.detail.analytics.chart', language)}
                          </button>
                        </li>
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link d-flex align-items-center gap-1${activeAnalyticsTab === 'contract' ? ' active' : ''}`}
                            onClick={() => setActiveAnalyticsTab('contract')}
                            type="button"
                            role="tab"
                          >
                            <DollarSign size={14} />
                            {translate('player.detail.analytics.contract', language)}
                          </button>
                        </li>
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link d-flex align-items-center gap-1${activeAnalyticsTab === 'transactions' ? ' active' : ''}`}
                            onClick={() => setActiveAnalyticsTab('transactions')}
                            type="button"
                            role="tab"
                          >
                            <ArrowRightLeft size={14} />
                            {translate('player.detail.analytics.transactions', language)}
                          </button>
                        </li>
                      </ul>
                    </div>
                    <div className="card-body">
                      {activeAnalyticsTab === 'chart' && (
                        <div>
                          <p className="text-muted small mb-3">
                            {translate('player.detail.analytics.chart.nhl.only', language)}
                          </p>
                          <PerformanceChart
                            seasonStats={seasonStats.data}
                            isGoalie={isGoalie}
                            language={language}
                          />
                        </div>
                      )}
                      {activeAnalyticsTab === 'contract' && (
                        <div className="d-flex flex-column gap-4">
                          <div className="row g-3">
                            <div className="col-12">
                              <InfoRow
                                icon={<DollarSign size={16} />}
                                label={translate('player.detail.salary', language)}
                                value={formatSalary(language, contractInfo?.salary)}
                              />
                            </div>
                          </div>
                          <div>
                            <div className="text-muted small text-uppercase fw-semibold mb-2">
                              {translate('player.detail.analytics.contract', language)}
                            </div>
                            <ContractValueBadge
                              contractInfo={contractInfo}
                              seasonStats={seasonStats.data}
                              isGoalie={isGoalie}
                              language={language}
                            />
                          </div>
                        </div>
                      )}
                      {activeAnalyticsTab === 'transactions' && (
                        <PlayerTransactionHistory
                          playerId={Number(playerId)}
                          leagueId={selectedLeague?.id ?? null}
                          language={language}
                        />
                      )}
                    </div>
                  </section>
                </div>

                <div className="col-12">
                  <div className="card shadow-sm historical-stats-card">
                    <div className="card-header bg-transparent d-flex justify-content-between align-items-center historical-stats-header">
                      <h2 className="h5 mb-0">{translate('player.detail.historical.stats', language)}</h2>
                      <div className="d-flex align-items-center gap-3 historical-stats-controls">
                        <label className="form-label mb-0 small">{translate('player.detail.select.season', language)}</label>
                        <select 
                          className="form-select form-select-sm"
                          value={selectedSeason}
                          onChange={(e) => setSelectedSeason(e.target.value)}
                          style={{ minWidth: '140px' }}
                        >
                          {availableSeasons.map((season) => (
                            <option key={season.value} value={season.value}>
                              {season.label} {season.isCurrent && `(${translate('player.detail.current.season', language)})`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="card-body">
                      {seasonStats.loading && (
                        <div className="d-flex justify-content-center align-items-center py-3">
                          <div className="spinner-border spinner-border-sm text-primary" role="status" aria-label={translate('player.detail.loading.season', language)} />
                          <span className="ms-2">{translate('player.detail.loading.season', language)}</span>
                        </div>
                      )}

                      {!seasonStats.loading && seasonStats.error && (
                        <div className="alert alert-warning" role="alert">
                          {seasonStats.error}
                        </div>
                      )}

                      {!seasonStats.loading && seasonStats.data && (
                        <SeasonStatsDisplayWithFilter
                          seasonStats={seasonStats.data}
                          selectedSeason={selectedSeason}
                          language={language}
                          isGoalie={isGoalie}
                        />
                      )}

                      {!seasonStats.loading && !seasonStats.data && !seasonStats.error && (
                        <div className="text-muted text-center py-3">
                          {translate('player.detail.no.season.data', language)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-transparent">
                      <h2 className="h5 mb-0">{translate('player.detail.contract', language)}</h2>
                    </div>
                    <div className="card-body">
                      {contractLoading ? (
                        <div className="d-flex justify-content-center align-items-center py-3">
                          <div className="spinner-border spinner-border-sm text-primary" role="status" aria-label={translate('loading', language)} />
                        </div>
                      ) : contractInfo ? (
                        <div className="row g-3">
                          <div className="col-12">
                            <InfoRow 
                              icon={<DollarSign size={16} />} 
                              label={translate('player.detail.salary', language)} 
                              value={formatSalary(language, contractInfo.salary)} 
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="text-muted text-center py-3">
                          {translate('player.detail.not.available', language)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          
          <section className="row g-4">
            <div className="col-12 col-lg-6">
              <CareerTotalsCard player={player} language={language} isGoalie={isGoalie} />
            </div>
            <div className="col-12 col-lg-6">
              <AwardsCard player={player} language={language} />
            </div>
          </section>

          <div className="card shadow-sm last-games-card">
            <div className="card-header bg-transparent d-flex justify-content-between align-items-center last-games-header">
              <h2 className="h5 mb-0">{translate('player.detail.last.games', language)}</h2>
              <span className="text-muted small last-games-subtitle">{translate('player.detail.last.games.subtitle', language)}</span>
            </div>
            <div className="table-wrapper">
              <table className="table mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{translate('date', language)}</th>
                    <th>{translate('player.detail.opponent', language)}</th>
                    {isGoalie ? (
                      <>
                        <th>{translate('player.detail.decision', language)}</th>
                        <th>{translate('stat.shotsAgainst', language)}</th>
                        <th>{translate('stat.saves', language)}</th>
                        <th>{translate('stat.goalsAgainst', language)}</th>
                        <th>{translate('stat.savePctg.short', language)}</th>
                      </>
                    ) : (
                      <>
                        <th>{translate('goals', language)}</th>
                        <th>{translate('assists', language)}</th>
                        <th>{translate('points', language)}</th>
                        <th>{translate('stat.shots', language)}</th>
                        <th>{translate('player.detail.plusminus.short', language)}</th>
                      </>
                    )}
                    <th>{translate('player.detail.toi', language)}</th>
                  </tr>
                </thead>
                <tbody>
                  {player.last5Games?.length ? (
                    player.last5Games.map((game, index) => (
                      <LastGameRow key={`${game.gameId}-${index}`} game={game} language={language} isGoalie={isGoalie} />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        {translate('player.detail.no.games', language)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* NHL Edge Advanced Stats — skaters & goalies */}
          <NhlEdgeStatsSection
            playerId={Number(playerId)}
            isGoalie={isGoalie}
            language={language}
          />

          {player.currentTeamRoster?.length ? (
            <section className="card shadow-sm">
              <div className="card-header bg-transparent">
                <h2 className="h5 mb-0">{translate('player.detail.current.roster', language)}</h2>
              </div>
              <div className="card-body">
                <div className="d-flex flex-wrap gap-2">
                  {player.currentTeamRoster.map((rosterPlayer) => (
                    <span key={rosterPlayer.playerId} className="badge roster-badge">
                      {getLocalized(rosterPlayer.firstName, language)} {getLocalized(rosterPlayer.lastName, language)}
                    </span>
                  ))}
                </div>
              </div>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}

function getLocalized(name: NhlPlayerName | undefined, language: 'fr' | 'en') {
  if (!name) return '—';
  return (language === 'fr' ? name.fr : name.default) || name.default || '—';
}

function formatDate(value: string, language: 'fr' | 'en') {
  try {
    return new Intl.DateTimeFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function calculateAge(birthDate: string): number {
  try {
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  } catch {
    return 0;
  }
}

function formatDateWithAge(birthDate: string, language: 'fr' | 'en') {
  const formattedDate = formatDate(birthDate, language);
  const age = calculateAge(birthDate);
  
  if (age > 0) {
    const ageText = language === 'fr' ? 'ans' : 'years old';
    return `${formattedDate} (${age} ${ageText})`;
  }
  
  return formattedDate;
}

function formatHeight(player: NhlPlayerLanding, language: 'fr' | 'en') {
  if (player.heightInCentimeters) {
    return `${player.heightInCentimeters} ${translate('player.detail.height.cm', language)}`;
  }
  if (player.heightInInches) {
    const feet = Math.floor(player.heightInInches / 12);
    const inches = player.heightInInches % 12;
    return `${feet}'${inches}"`;
  }
  return translate('player.detail.not.available', language);
}

function formatWeight(player: NhlPlayerLanding, language: 'fr' | 'en') {
  if (player.weightInKilograms) {
    return `${player.weightInKilograms} ${translate('player.detail.weight.kg', language)}`;
  }
  if (player.weightInPounds) {
    return `${player.weightInPounds} ${translate('player.detail.weight.lbs', language)}`;
  }
  return translate('player.detail.not.available', language);
}

function formatShoots(player: NhlPlayerLanding, language: 'fr' | 'en') {
  const mapping: Record<string, string> = {
    L: translate('player.detail.shoots.left', language),
    R: translate('player.detail.shoots.right', language),
    C: translate('player.detail.shoots.catch', language),
  };
  return player.shootsCatches ? mapping[player.shootsCatches] || player.shootsCatches : translate('player.detail.not.available', language);
}

function formatDraft(player: NhlPlayerLanding, language: 'fr' | 'en') {
  const draft = player.draftDetails;
  if (!draft?.year) {
    return translate('player.detail.not.available', language);
  }

  const pieces = [
    draft.year,
    draft.teamAbbrev,
    draft.round ? `${translate('player.detail.round', language)} ${draft.round}` : null,
    draft.overallPick ? `${draft.overallPick}${translate('player.detail.pick.suffix', language)}` : null,
  ].filter(Boolean);

  return pieces.join(' · ');
}

function formatBirthplace(player: NhlPlayerLanding, language: 'fr' | 'en') {
  const city = getLocalized(player.birthCity, language);
  const province = getLocalized(player.birthStateProvince, language);
  const country = player.birthCountry ?? '';
  const parts = [city !== '—' ? city : null, province !== '—' ? province : null, country || null].filter(Boolean);
  if (!parts.length) {
    return translate('player.detail.not.available', language);
  }
  return parts.join(', ');
}

function formatSalary(language: 'fr' | 'en', salary?: number) {
  if (!salary) return translate('player.detail.not.available', language);
  return `$${salary.toLocaleString()}`;
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="d-flex align-items-center gap-3">
      <div className="icon-circle bg-primary-subtle text-primary-emphasis d-inline-flex align-items-center justify-content-center">
        {icon}
      </div>
      <div className="flex-grow-1">
        <div className="text-muted small text-uppercase fw-semibold">{label}</div>
        <div className="fw-bold fs-6">{value}</div>
      </div>
    </div>
  );
}

interface StatsCardProps {
  title: string;
  featuredStats?: NhlFeaturedStats;
  language: 'fr' | 'en';
  isGoalie: boolean;
}

function StatsCard({ title, featuredStats, language, isGoalie }: StatsCardProps) {
  const seasonLabel = featuredStats?.season?.toString();
  const regularSeason = featuredStats?.regularSeason;
  const playoffs = featuredStats?.playoffs;
  const fields = isGoalie ? GOALIE_STAT_FIELDS : SKATER_STAT_FIELDS;

  return (
    <div className="card h-100 shadow-sm">
      <div className="card-header bg-transparent d-flex justify-content-between align-items-center">
        <h2 className="h5 mb-0">{title}</h2>
  {seasonLabel && <span className="badge season-badge">{seasonLabel}</span>}
      </div>
      <div className="card-body">
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <StatsTable
              title={translate('player.detail.regular.season', language)}
              seasonStats={regularSeason?.subSeason}
              careerStats={regularSeason?.career}
              fields={fields}
              language={language}
            />
          </div>
          <div className="col-12 col-lg-6">
            <StatsTable
              title={translate('player.detail.playoffs', language)}
              seasonStats={playoffs?.subSeason}
              careerStats={playoffs?.career}
              fields={fields}
              language={language}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface StatsTableProps {
  title: string;
  seasonStats?: Record<string, number | string>;
  careerStats?: Record<string, number | string>;
  fields: Array<{ key: string; valueKey?: string }>;
  language: 'fr' | 'en';
}

const SKATER_STAT_FIELDS: Array<{ key: string; valueKey?: string }> = [
  { key: 'gamesPlayed' },
  { key: 'goals' },
  { key: 'assists' },
  { key: 'points' },
  { key: 'plusMinus', valueKey: 'plusMinus' },
  { key: 'powerPlayGoals' },
  { key: 'powerPlayPoints' },
  { key: 'shorthandedGoals' },
  { key: 'shorthandedPoints' },
  { key: 'gameWinningGoals' },
  { key: 'shots' },
  { key: 'shootingPctg', valueKey: 'shootingPctg' },
  { key: 'pim', valueKey: 'pim' },
];

const GOALIE_STAT_FIELDS: Array<{ key: string; valueKey?: string }> = [
  { key: 'gamesPlayed' },
  { key: 'wins' },
  { key: 'losses' },
  { key: 'otLosses' },
  { key: 'goalsAgainstAvg', valueKey: 'goalsAgainstAvg' },
  { key: 'savePctg', valueKey: 'savePctg' },
  { key: 'shutouts' },
  { key: 'shotsAgainst' },
  { key: 'saves' },
];

function StatsTable({ title, seasonStats, careerStats, fields, language }: StatsTableProps) {
  return (
    <div className="stats-table">
      <h3 className="h6 text-uppercase text-muted mb-2">{title}</h3>
      <table className="table table-sm align-middle">
        <thead>
          <tr>
            <th>{translate('player.detail.stat.label', language)}</th>
            <th className="text-end">{translate('player.detail.stat.season', language)}</th>
            <th className="text-end">{translate('player.detail.stat.career', language)}</th>
          </tr>
        </thead>
        <tbody>
          {fields.map(({ key, valueKey }) => {
            const seasonValue = seasonStats?.[key as keyof typeof seasonStats];
            const careerValue = careerStats?.[key as keyof typeof careerStats];

            if (seasonValue == null && careerValue == null) {
              return null;
            }

            return (
              <tr key={key}>
                <td>{translate(`stat.${key}`, language)}</td>
                <td className="text-end">{formatStatValue(valueKey ?? key, seasonValue)}</td>
                <td className="text-end">{formatStatValue(valueKey ?? key, careerValue)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatStatValue(key: string, rawValue?: number | string) {
  if (rawValue == null) return '—';
  const value = typeof rawValue === 'string' ? Number(rawValue) : rawValue;

  if (typeof value !== 'number' || Number.isNaN(value)) {
    return typeof rawValue === 'string' ? rawValue : '—';
  }

  if (key === 'shootingPctg') {
    return `${(value * 100).toFixed(1)}%`;
  }
  if (key === 'savePctg') {
    return value.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  }
  if (key === 'goalsAgainstAvg') {
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return value.toLocaleString();
}

interface LastGameRowProps {
  game: NhlLastGame;
  language: 'fr' | 'en';
  isGoalie: boolean;
}

function LastGameRow({ game, language, isGoalie }: LastGameRowProps) {
  if (isGoalie) {
    return (
      <tr>
        <td>{game.gameDate ? formatDate(game.gameDate, language) : '—'}</td>
        <td>{game.opponentAbbrev || '—'}</td>
        <td>{formatDecision(game.decision, language)}</td>
        <td>{formatNumber(game.shotsAgainst)}</td>
        <td>{formatNumber(game.saves)}</td>
        <td>{formatNumber(game.goalsAgainst)}</td>
        <td>{formatSavePercentage(game.savePctg)}</td>
        <td>{game.toi || '—'}</td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{game.gameDate ? formatDate(game.gameDate, language) : '—'}</td>
      <td>{game.opponentAbbrev || '—'}</td>
      <td>{defaultZero(game.goals)}</td>
      <td>{defaultZero(game.assists)}</td>
      <td>{defaultZero(game.points)}</td>
      <td>{defaultZero(game.shots)}</td>
      <td>{formatSigned(game.plusMinus)}</td>
      <td>{game.toi || '—'}</td>
    </tr>
  );
}

function defaultZero(value?: number) {
  return value != null ? value : 0;
}

function formatSigned(value?: number) {
  if (value == null) return '—';
  if (value > 0) return `+${value}`;
  return value.toString();
}

function formatNumber(value?: number) {
  if (value == null) return '—';
  return value.toLocaleString();
}

function formatSavePercentage(value?: number) {
  if (value == null) return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function formatDecision(decision: string | undefined, language: 'fr' | 'en') {
  if (!decision) return '—';
  const normalized = decision.toUpperCase();

  switch (normalized) {
    case 'W':
      return translate('player.detail.decision.win', language);
    case 'L':
      return translate('player.detail.decision.loss', language);
    case 'OT':
      return translate('player.detail.decision.ot', language);
    case 'SO':
      return translate('player.detail.decision.so', language);
    default:
      return decision;
  }
}

interface CareerTotalsCardProps {
  player: NhlPlayerLanding;
  language: 'fr' | 'en';
  isGoalie: boolean;
}

function CareerTotalsCard({ player, language, isGoalie }: CareerTotalsCardProps) {
  const regular = player.careerTotals?.regularSeason;
  const playoffs = player.careerTotals?.playoffs;

  if (!regular && !playoffs) {
    return null;
  }

  const rows: Array<{ key: string; regular: number | string | undefined; playoffs: number | string | undefined }> = (() => {
    if (isGoalie) {
      return [
        { key: 'gamesPlayed', regular: regular?.gamesPlayed, playoffs: playoffs?.gamesPlayed },
        { key: 'wins', regular: regular?.wins, playoffs: playoffs?.wins },
        { key: 'losses', regular: regular?.losses, playoffs: playoffs?.losses },
        { key: 'otLosses', regular: regular?.otLosses, playoffs: playoffs?.otLosses },
        { key: 'goalsAgainst', regular: regular?.goalsAgainst, playoffs: playoffs?.goalsAgainst },
        { key: 'goalsAgainstAvg', regular: regular?.goalsAgainstAvg, playoffs: playoffs?.goalsAgainstAvg },
        { key: 'savePctg', regular: regular?.savePctg, playoffs: playoffs?.savePctg },
        { key: 'shutouts', regular: regular?.shutouts, playoffs: playoffs?.shutouts },
        { key: 'shotsAgainst', regular: regular?.shotsAgainst, playoffs: playoffs?.shotsAgainst },
        { key: 'saves', regular: regular?.saves, playoffs: playoffs?.saves },
      ];
    }

    // Skater rows
    const skaterRows: Array<{ key: string; regular: number | string | undefined; playoffs: number | string | undefined }> = [
      { key: 'gamesPlayed', regular: regular?.gamesPlayed, playoffs: playoffs?.gamesPlayed },
      { key: 'goals', regular: regular?.goals, playoffs: playoffs?.goals },
      { key: 'assists', regular: regular?.assists, playoffs: playoffs?.assists },
      { key: 'points', regular: regular?.points, playoffs: playoffs?.points },
      { key: 'plusMinus', regular: regular?.plusMinus, playoffs: playoffs?.plusMinus },
      { key: 'powerPlayGoals', regular: regular?.powerPlayGoals, playoffs: playoffs?.powerPlayGoals },
      { key: 'powerPlayPoints', regular: regular?.powerPlayPoints, playoffs: playoffs?.powerPlayPoints },
      { key: 'shorthandedGoals', regular: regular?.shorthandedGoals, playoffs: playoffs?.shorthandedGoals },
      { key: 'shorthandedPoints', regular: regular?.shorthandedPoints, playoffs: playoffs?.shorthandedPoints },
      { key: 'gameWinningGoals', regular: regular?.gameWinningGoals, playoffs: playoffs?.gameWinningGoals },
      { key: 'shots', regular: regular?.shots, playoffs: playoffs?.shots },
    ];

    // Calculer le pourcentage de tir (goals / shots) en décimal (ex: 0.123) pour être compatible avec formatStatValue
    const parseNumber = (v: any): number | undefined => {
      if (v == null) return undefined;
      const n = Number(v);
      return Number.isNaN(n) ? undefined : n;
    };

    const regularGoals = parseNumber(regular?.goals);
    const regularShots = parseNumber(regular?.shots);
    const playoffsGoals = parseNumber(playoffs?.goals);
    const playoffsShots = parseNumber(playoffs?.shots);

    const regularShootingPct = (regularShots && regularShots > 0 && regularGoals != null) ? regularGoals / regularShots : undefined;
    const playoffsShootingPct = (playoffsShots && playoffsShots > 0 && playoffsGoals != null) ? playoffsGoals / playoffsShots : undefined;

    skaterRows.push({ key: 'shootingPctg', regular: regularShootingPct, playoffs: playoffsShootingPct });

    return skaterRows;
  })();

  return (
    <div className="card h-100 shadow-sm">
      <div className="card-header bg-transparent">
        <h2 className="h5 mb-0">{translate('player.detail.career.totals', language)}</h2>
      </div>
      <div className="card-body">
        <table className="table table-sm align-middle mb-0">
          <thead>
            <tr>
              <th>{translate('player.detail.stat.label', language)}</th>
              <th className="text-end">{translate('player.detail.regular.season.short', language)}</th>
              <th className="text-end">{translate('player.detail.playoffs.short', language)}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{translate(`stat.${row.key}`, language)}</td>
                <td className="text-end">{formatStatValue(row.key, row.regular)}</td>
                <td className="text-end">{formatStatValue(row.key, row.playoffs)}</td>
              </tr>
            ))}
            {regular?.avgToi && (
              <tr>
                <td>{translate('stat.avgToi', language)}</td>
                <td className="text-end">{regular.avgToi}</td>
                <td className="text-end">{playoffs?.avgToi ?? '—'}</td>
              </tr>
            )}
            {!isGoalie && regular?.faceoffWinningPctg != null && (
              <tr>
                <td>{translate('stat.faceoffWinningPctg', language)}</td>
                <td className="text-end">{(Number(regular.faceoffWinningPctg) * 100).toFixed(1)}%</td>
                <td className="text-end">{playoffs?.faceoffWinningPctg != null ? (Number(playoffs.faceoffWinningPctg) * 100).toFixed(1) + '%' : '—'}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface AwardsCardProps {
  player: NhlPlayerLanding;
  language: 'fr' | 'en';
}

function AwardsCard({ player, language }: AwardsCardProps) {
  if (!player.awards?.length) {
    return (
      <div className="card h-100 shadow-sm">
        <div className="card-header bg-transparent">
          <h2 className="h5 mb-0">{translate('player.detail.awards', language)}</h2>
        </div>
        <div className="card-body text-muted d-flex align-items-center justify-content-center">
          {translate('player.detail.awards.none', language)}
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100 shadow-sm">
      <div className="card-header bg-transparent d-flex align-items-center gap-2">
        <Award size={18} className="text-warning" />
        <h2 className="h5 mb-0">{translate('player.detail.awards', language)}</h2>
      </div>
      <div className="card-body d-flex flex-column gap-3">
        {player.awards?.map((award, index) => {
          const seasons = Array.isArray((award as any).seasons) ? (award as any).seasons : [];
          return (
            <div key={`${(award as any).trophy?.default}-${index}`} className="border rounded-3 p-3">
              <div className="fw-semibold mb-2">
                {getLocalized((award as any).trophy, language)}
              </div>
              <div className="d-flex flex-wrap gap-2">
                {seasons.map((season: any) => (
                  <span key={`${season.seasonId}-${season.gameTypeId || ''}`} className="badge award-badge">
                    {season.seasonId}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface SeasonStatsDisplayProps {
  seasonStats: NhlSeasonStats;
  language: 'fr' | 'en';
  isGoalie: boolean;
}

interface SeasonStatsDisplayWithFilterProps {
  seasonStats: NhlSeasonStats;
  selectedSeason: string;
  language: 'fr' | 'en';
  isGoalie: boolean;
}

function SeasonStatsDisplayWithFilter({ seasonStats, selectedSeason, language, isGoalie }: SeasonStatsDisplayWithFilterProps) {
  const seasonTotals = seasonStats.seasonTotals || [];
  
  if (seasonTotals.length === 0) {
    return (
      <div className="text-muted text-center py-3">
        {translate('player.detail.no.season.data', language)}
      </div>
    );
  }

  // Convertir la saison sélectionnée en nombre pour la comparaison
  const selectedSeasonNumber = parseInt(selectedSeason);
  
  // Filtrer par saison sélectionnée
  const selectedSeasonData = seasonTotals.filter(s => s.season === selectedSeasonNumber);
  
  if (selectedSeasonData.length === 0) {
    return (
      <div className="text-muted text-center py-3">
        {translate('player.detail.no.season.data', language)} ({selectedSeason})
      </div>
    );
  }

  // Regrouper par ligue (leagueAbbrev) pour afficher chaque ligue séparément
  type SeasonEntry = typeof selectedSeasonData[0];
  const leagueGroups = new Map<string, { regular?: SeasonEntry; playoffs?: SeasonEntry }>();

  for (const entry of selectedSeasonData) {
    const league = entry.leagueAbbrev || 'N/A';
    if (!leagueGroups.has(league)) {
      leagueGroups.set(league, {});
    }
    const group = leagueGroups.get(league)!;
    if (entry.gameTypeId === 2) group.regular = entry;
    else if (entry.gameTypeId === 3) group.playoffs = entry;
  }

  const fields = isGoalie ? GOALIE_STAT_FIELDS : SKATER_STAT_FIELDS;

  return (
    <div className="d-flex flex-column gap-4">
      {Array.from(leagueGroups.entries()).map(([league, { regular, playoffs }]) => (
        <div key={league}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="badge bg-secondary fs-6 px-2 py-1">{league}</span>
          </div>
          <div className="table-wrapper">
            <table className="table table-sm align-middle">
              <thead className="table-light">
                <tr>
                  <th>{translate('player.detail.stat.label', language)}</th>
                  <th className="text-end">{translate('player.detail.regular.season', language)}</th>
                  <th className="text-end">{translate('player.detail.playoffs', language)}</th>
                </tr>
              </thead>
              <tbody>
                {fields.map(({ key }) => (
                  <tr key={key}>
                    <td>{translate(`stat.${key}`, language)}</td>
                    <td className="text-end">
                      {formatStatValue(key, regular?.[key as keyof typeof regular])}
                    </td>
                    <td className="text-end">
                      {formatStatValue(key, playoffs?.[key as keyof typeof playoffs])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function SeasonStatsDisplay({ seasonStats, language, isGoalie }: SeasonStatsDisplayProps) {
  const seasonTotals = seasonStats.seasonTotals || [];
  
  console.log('📊 SeasonStatsDisplay:', { seasonStats, seasonTotals, isGoalie });
  
  if (seasonTotals.length === 0) {
    console.log('Aucune donnée de saison trouvée dans seasonTotals');
    return (
      <div className="text-muted text-center py-3">
        {translate('player.detail.no.season.data', language)}
      </div>
    );
  }

  // Chercher les stats de saison régulière et playoffs dans seasonTotals
  const regularSeasonStats = seasonTotals.find(s => s.gameTypeId === 2);
  const playoffStats = seasonTotals.find(s => s.gameTypeId === 3);
  
  console.log('📈 Stats trouvées dans seasonTotals:', { 
    regularSeasonStats, 
    playoffStats,
    availableSeasons: seasonTotals.map(s => ({ season: s.season, gameTypeId: s.gameTypeId }))
  });
  
  const fields = isGoalie ? GOALIE_STAT_FIELDS : SKATER_STAT_FIELDS;

  return (
    <div className="row g-3">
      <div className="col-12">
          <div className="table-wrapper">
            <table className="table table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th>{translate('player.detail.stat.label', language)}</th>
                <th className="text-end">{translate('player.detail.regular.season', language)}</th>
                <th className="text-end">{translate('player.detail.playoffs', language)}</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(({ key }) => (
                <tr key={key}>
                  <td>{translate(`stat.${key}`, language)}</td>
                  <td className="text-end">
                    {formatStatValue(key, regularSeasonStats?.[key as keyof typeof regularSeasonStats])}
                  </td>
                  <td className="text-end">
                    {formatStatValue(key, playoffStats?.[key as keyof typeof playoffStats])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
